import { ListingInfo } from '@apeiron/common'
import { MayNull, useAccountContext } from '@apeiron/temp'
import { useMktErrorHandling } from '@apeiron/wallet'
import { Box, styled } from '@mui/material'
import ApeironPriceDuration from '@src/components/share/apeiron/ApeironPriceDuration'
import TradeBuyButton from '@src/components/share/trade/TradeBuyButton'
import TradeCancelSellButton from '@src/components/share/trade/TradeCancelSellButton'
import TradePrice, {
  TradePriceProps
} from '@src/components/share/trade/TradePrice'
import TradeUpdateSellButton from '@src/components/share/trade/TradeUpdatelSellButton'
import { FC, useCallback } from 'react'

const Container = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  gap: 32px;
  padding-left: 10px;
  ${props => props.theme.breakpoints.down('res1280')} {
    justify-content: center;
    margin: 0px auto;
    max-width: 780px;
  }
`

const SellButtons = styled(Box)`
  column-gap: 32px;
  display: flex;
  padding-left: 9px;
  ${props => props.theme.breakpoints.up('res1280')} {
    flex-wrap: wrap;
    flex: 1;
    row-gap: 10px;
  }
  ${props => props.theme.breakpoints.down('res1280')} {
    justify-content: space-between;
  }
`

export const PlanetOnOfferSection: FC<PlanetOnOfferSectionProps> = props => {
  const {
    className,
    listingInfo,
    planetId,
    priceViewProps,
    shouldShowBuy,
    shouldShowCancelSell,
    onBuy,
    onCancelSell,
    onUpdateSell
  } = props

  const { isRoninAccount } = useAccountContext()

  const { showErrorPopup } = useMktErrorHandling()

  const onBuyClick = useCallback(async () => {
    try {
      const priceForAllowance =
        listingInfo?.endPrice || priceViewProps?.price || 0

      if (onBuy && priceForAllowance > 0) {
        await onBuy(planetId, priceForAllowance)
      }
    } catch (e) {
      showErrorPopup(e)
    }
  }, [
    listingInfo?.endPrice,
    priceViewProps?.price,
    onBuy,
    planetId,
    showErrorPopup
  ])

  const onCancelSellClick = useCallback(async () => {
    if (onCancelSell && listingInfo) {
      await onCancelSell(planetId, listingInfo)
    }
  }, [onCancelSell, planetId, listingInfo])

  const onUpdateSellClick = useCallback(async () => {
    if (onUpdateSell) {
      await onUpdateSell(planetId)
    }
  }, [onUpdateSell, planetId])

  return (
    <Container className={className}>
      {priceViewProps && (
        <>
          <ApeironPriceDuration listingInfo={listingInfo} />
          <TradePrice {...priceViewProps} />
        </>
      )}
      {shouldShowBuy && <TradeBuyButton onClick={onBuyClick} />}
      {shouldShowCancelSell && (
        <SellButtons>
          <TradeCancelSellButton onClick={onCancelSellClick} />
          {!isRoninAccount && (
            <TradeUpdateSellButton onClick={onUpdateSellClick} />
          )}
        </SellButtons>
      )}
    </Container>
  )
}

export type PlanetOnOfferSectionProps = {
  className?: string
  listingInfo?: MayNull<ListingInfo>
  planetId: string
  priceViewProps?: TradePriceProps
  shouldShowBuy?: boolean
  shouldShowCancelSell?: boolean
  onBuy?: (planetId: string, price: number) => void
  onCancelSell?: (planetId: string, planetListingInfo: ListingInfo) => void
  onUpdateSell?: (planetId: string) => void
}
